/**
 * AdminServiceAreasPage — Phase F.6 admin tab for managing the metro
 * coverage allowlist (the ZIPs the public Map page treats as "served").
 *
 * Two columns on desktop:
 *   - LEFT: current ZIPs grid + add/remove form (reason required).
 *   - RIGHT: audit feed (last 50 changes, who/when/why).
 *
 * On mobile both stack. Admin-only (rendered behind the existing
 * `/admin` ProtectedRoute(requireAdmin) gate, which 403s non-admins
 * at the route level — no double gate needed here).
 *
 * Why no separate "are you sure?" modal:
 *   The reason field IS the confirmation. An admin can't apply a
 *   diff without writing a 3+ char rationale that lands in the
 *   90-day audit log. Click-anywhere fat-finger mistakes still get
 *   blocked because the Apply button is disabled until both a diff
 *   and a reason are present.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE } from "@/lib/api";
import { Plus, Minus, History, Loader2, AlertCircle } from "lucide-react";
import { toMetroDisplay } from "@/lib/metroDisplay";

const DEFAULT_METRO = "omaha";

function parseZipInput(text) {
  // Accept newline OR comma separated; ignore whitespace/duplicates;
  // returns a unique 5-digit array. Invalid tokens are surfaced so
  // the admin sees exactly what was rejected.
  const tokens = (text || "")
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const valid = [];
  const invalid = [];
  const seen = new Set();
  for (const t of tokens) {
    if (/^\d{5}$/.test(t)) {
      if (!seen.has(t)) {
        valid.push(t);
        seen.add(t);
      }
    } else {
      invalid.push(t);
    }
  }
  return { valid, invalid };
}

export default function AdminServiceAreasPage() {
  // Coverage-Map multi-metro readiness — replaced the const-only
  // `useState(DEFAULT_METRO)` with a real setter so the admin can
  // switch between the 5 launch metros (Omaha, KC, DFW, Phoenix,
  // Denver). Metro list is DYNAMIC — fetched from /public/metros so
  // new metros seeded in the DB render new options with zero
  // frontend code change.
  const [metro, setMetro] = useState(DEFAULT_METRO);
  const [metroOptions, setMetroOptions] = useState([]);
  const [zips, setZips] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Form state
  const [addText, setAddText] = useState("");
  const [removeText, setRemoveText] = useState("");
  const [reason, setReason] = useState("");

  const addParsed = useMemo(() => parseZipInput(addText), [addText]);
  const removeParsed = useMemo(() => parseZipInput(removeText), [removeText]);
  const hasDiff = addParsed.valid.length + removeParsed.valid.length > 0;
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 240;
  const submitOk = hasDiff && reasonOk && !bulkBusy;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("pp_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [zR, aR] = await Promise.all([
        fetch(`${API_BASE}/admin/coverage/zips?metro=${metro}`, { headers }),
        fetch(
          `${API_BASE}/admin/coverage/zip-audit?metro=${metro}&limit=50`,
          { headers },
        ),
      ]);
      if (!zR.ok || !aR.ok) {
        throw new Error("Couldn't load service area data.");
      }
      const zJ = await zR.json();
      const aJ = await aR.json();
      setZips(zJ.coverage_zips || []);
      setAudit(aJ.audit || []);
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }, [metro]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Metro list fetch — Coverage-Map multi-metro readiness.
  // /public/metros returns all active launch metros (sort-order sorted).
  // Adding a new metro row in the DB renders a new <option> here with
  // zero code change (dynamic proof asserted in the labels test file).
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/public/metros`);
        if (!r.ok) return;
        const j = await r.json();
        const rows = (j.metros || [])
          .map((m) => ({ code: m.code, label: m.label }))
          .filter((m) => m.code);
        setMetroOptions(rows);
      } catch (_e) {
        // Silent — falls back to hardcoded DEFAULT_METRO selection.
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBulkResult(null);
    if (!submitOk) return;
    setBulkBusy(true);
    try {
      const token = localStorage.getItem("pp_token");
      const r = await fetch(`${API_BASE}/admin/coverage/zips`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          metro,
          zips_add: addParsed.valid,
          zips_remove: removeParsed.valid,
          reason: reason.trim(),
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        throw new Error(
          typeof j?.detail === "string"
            ? j.detail
            : "Couldn't apply the change.",
        );
      }
      setBulkResult(j);
      // Clear inputs after a successful apply.
      setAddText("");
      setRemoveText("");
      setReason("");
      await fetchAll();
    } catch (e2) {
      setError(e2.message || "Something went wrong.");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <main
      className="px-4 md:px-8 py-8 max-w-[1280px] mx-auto"
      data-testid="admin-service-areas"
    >
      <header className="mb-6">
        <div className="pp-kick" style={{ fontSize: 11, color: "var(--pp-text-soft)" }}>
          Coverage admin
        </div>
        <h1
          className="pp-h1 mt-1"
          style={{ fontSize: 28, letterSpacing: "-0.02em", margin: 0 }}
        >
          Service Areas — {toMetroDisplay(metro)}
        </h1>
        {/* Metro picker — Coverage-Map multi-metro readiness. Plain
            <select> matching existing admin form styling; density
            over flair. Options DYNAMICALLY populated from
            /public/metros so new metros in the DB add options with
            zero frontend change. */}
        <div className="mt-3 flex items-center gap-2">
          <label
            htmlFor="admin-metro-picker"
            className="pp-sm"
            style={{ color: "var(--pp-text-soft)" }}
          >
            Metro:
          </label>
          <select
            id="admin-metro-picker"
            data-testid="admin-metro-picker"
            value={metro}
            onChange={(e) => setMetro(e.target.value)}
            className="pp-select rounded border px-3 py-1.5 text-sm"
            style={{ minWidth: 180 }}
          >
            {metroOptions.length === 0 && (
              <option value={metro}>{toMetroDisplay(metro)}</option>
            )}
            {metroOptions.map((m) => (
              <option key={m.code} value={m.code} data-testid={`admin-metro-option-${m.code}`}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <p
          className="pp-sm mt-2"
          style={{ color: "var(--pp-text-soft)", maxWidth: 720 }}
        >
          Manage which ZIPs the metro treats as part of its launch footprint.
          Adds and removes are bulk-applied and audited (90-day retention). The
          public Map page reflects changes within seconds.
        </p>
      </header>

      {error && (
        <div
          className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm mb-4 flex items-start gap-2"
          style={{ color: "#B91C1C" }}
          data-testid="admin-service-areas-error"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {bulkResult && (
        <div
          className="rounded-lg border px-4 py-3 text-sm mb-4"
          style={{
            background: "#ECFDF5",
            borderColor: "#A7F3D0",
            color: "#065F46",
          }}
          data-testid="admin-service-areas-result"
        >
          <strong>Applied.</strong>{" "}
          Added {bulkResult.applied?.added?.length || 0} ·{" "}
          Removed {bulkResult.applied?.removed?.length || 0} ·{" "}
          Skipped {(bulkResult.skipped?.already_present?.length || 0) +
            (bulkResult.skipped?.already_absent?.length || 0)}.
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <section
          className="rounded-2xl p-5 lg:p-6"
          style={{
            background: "var(--pp-v3-surface)",
            border: "1px solid var(--pp-line)",
          }}
          data-testid="admin-service-areas-main"
        >
          <h2
            className="pp-h2"
            style={{ fontSize: 18, margin: 0, letterSpacing: "-0.01em" }}
          >
            Current ZIPs ({loading ? "…" : zips.length})
          </h2>
          <div
            className="mt-3 flex flex-wrap gap-2"
            data-testid="admin-service-areas-current"
          >
            {loading && (
              <div
                className="flex items-center gap-2 pp-sm"
                style={{ color: "var(--pp-text-soft)" }}
              >
                <Loader2 size={14} className="animate-spin" />
                Loading…
              </div>
            )}
            {!loading && zips.length === 0 && (
              <div
                className="pp-sm"
                style={{ color: "var(--pp-text-soft)" }}
              >
                No ZIPs in this metro yet.
              </div>
            )}
            {!loading && zips.map((z) => (
              <span
                key={z}
                className="inline-flex items-center px-2.5 py-1 rounded-md"
                style={{
                  background: "var(--pp-bg)",
                  border: "1px solid var(--pp-line)",
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 13,
                  color: "var(--pp-text)",
                }}
                data-testid={`admin-service-areas-zip-${z}`}
              >
                {z}
              </span>
            ))}
          </div>

          {/* Bulk form */}
          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1">
                <span
                  className="pp-kick"
                  style={{ fontSize: 11, color: "var(--pp-text-soft)" }}
                >
                  <Plus size={11} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 4 }} />
                  ZIPs to add
                </span>
                <textarea
                  value={addText}
                  onChange={(e) => setAddText(e.target.value)}
                  rows={4}
                  placeholder="68130, 68114&#10;68022"
                  className="rounded-md px-3 py-2 text-sm font-mono"
                  style={{
                    background: "var(--pp-bg)",
                    border: "1px solid var(--pp-line)",
                    color: "var(--pp-text)",
                    resize: "vertical",
                  }}
                  data-testid="admin-service-areas-add-input"
                />
                <span style={{ fontSize: 11, color: "var(--pp-text-soft)" }}>
                  {addParsed.valid.length} valid
                  {addParsed.invalid.length > 0
                    ? ` · ${addParsed.invalid.length} invalid: ${addParsed.invalid.join(", ")}`
                    : ""}
                </span>
              </label>
              <label className="flex flex-col gap-1">
                <span
                  className="pp-kick"
                  style={{ fontSize: 11, color: "var(--pp-text-soft)" }}
                >
                  <Minus size={11} style={{ display: "inline-block", verticalAlign: "middle", marginRight: 4 }} />
                  ZIPs to remove
                </span>
                <textarea
                  value={removeText}
                  onChange={(e) => setRemoveText(e.target.value)}
                  rows={4}
                  placeholder="68142&#10;68152"
                  className="rounded-md px-3 py-2 text-sm font-mono"
                  style={{
                    background: "var(--pp-bg)",
                    border: "1px solid var(--pp-line)",
                    color: "var(--pp-text)",
                    resize: "vertical",
                  }}
                  data-testid="admin-service-areas-remove-input"
                />
                <span style={{ fontSize: 11, color: "var(--pp-text-soft)" }}>
                  {removeParsed.valid.length} valid
                  {removeParsed.invalid.length > 0
                    ? ` · ${removeParsed.invalid.length} invalid: ${removeParsed.invalid.join(", ")}`
                    : ""}
                </span>
              </label>
            </div>
            <label className="flex flex-col gap-1">
              <span
                className="pp-kick"
                style={{ fontSize: 11, color: "var(--pp-text-soft)" }}
              >
                Reason (3-240 chars, required)
              </span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={240}
                placeholder="Why are these ZIPs changing? (audit log)"
                className="rounded-md px-3 py-2 text-sm"
                style={{
                  background: "var(--pp-bg)",
                  border: "1px solid var(--pp-line)",
                  color: "var(--pp-text)",
                }}
                data-testid="admin-service-areas-reason"
              />
            </label>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={!submitOk}
                className="rounded-md px-4 py-2 text-sm font-semibold"
                style={{
                  background: submitOk ? "var(--pp-orange)" : "var(--pp-bg)",
                  color: submitOk ? "white" : "var(--pp-text-soft)",
                  border: "1px solid " + (submitOk ? "var(--pp-orange)" : "var(--pp-line)"),
                  cursor: submitOk ? "pointer" : "not-allowed",
                }}
                data-testid="admin-service-areas-submit"
              >
                {bulkBusy ? "Applying…" : `Apply (${addParsed.valid.length}↑ ${removeParsed.valid.length}↓)`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddText("");
                  setRemoveText("");
                  setReason("");
                  setBulkResult(null);
                }}
                className="rounded-md px-4 py-2 text-sm"
                style={{
                  background: "var(--pp-bg)",
                  border: "1px solid var(--pp-line)",
                  color: "var(--pp-text)",
                }}
                data-testid="admin-service-areas-clear"
              >
                Clear
              </button>
            </div>
          </form>
        </section>

        <aside
          className="rounded-2xl p-5"
          style={{
            background: "var(--pp-v3-surface)",
            border: "1px solid var(--pp-line)",
          }}
          data-testid="admin-service-areas-audit"
        >
          <div className="flex items-center gap-2 mb-3">
            <History size={16} style={{ color: "var(--pp-text-soft)" }} />
            <h2
              className="pp-h2"
              style={{ fontSize: 15, margin: 0, letterSpacing: "-0.01em" }}
            >
              Recent changes
            </h2>
          </div>
          {loading && (
            <div
              className="pp-sm flex items-center gap-2"
              style={{ color: "var(--pp-text-soft)" }}
            >
              <Loader2 size={12} className="animate-spin" />
              Loading…
            </div>
          )}
          {!loading && audit.length === 0 && (
            <div
              className="pp-sm"
              style={{ color: "var(--pp-text-soft)" }}
              data-testid="admin-service-areas-audit-empty"
            >
              No changes in the last 90 days.
            </div>
          )}
          {!loading && audit.length > 0 && (
            <ul className="flex flex-col gap-2">
              {audit.map((r) => (
                <li
                  key={r.id}
                  className="rounded-md px-3 py-2"
                  style={{
                    background: "var(--pp-bg)",
                    border: "1px solid var(--pp-line)",
                    fontSize: 12,
                  }}
                  data-testid={`admin-service-areas-audit-row-${r.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 600, color: "var(--pp-text)" }}>
                      {r.action === "add" ? "+ " : "− "}
                      {r.zip}
                    </span>
                    <span style={{ color: "var(--pp-text-soft)" }}>
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1" style={{ color: "var(--pp-text-soft)" }}>
                    {r.admin_email} · <em>{r.reason}</em>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </main>
  );
}
